import { makeObservable, observable } from "mobx"

import { LcAddress } from "@/domains/core"

import { MinimalPoolStore } from "./minimal-pool-store"

export class MinimalPoolRepository {
  pools = new Map<string, MinimalPoolStore>()

  constructor() {
    makeObservable(this, {
      pools: observable,
    })
  }

  // Add pool to repository
  add(pool: MinimalPoolStore): void {
    const existing = this.find(pool.address)
    if (existing) {
      return
    }

    this.pools.set(pool.address.toString(), pool)
  }

  // Simple find method - returns actual ClassicPoolStore instance
  find(address: LcAddress): MinimalPoolStore | undefined {
    return this.pools.get(address.toString())
  }

  // Find pool by gauge address
  findByGauge(gauge: LcAddress): MinimalPoolStore | undefined {
    return Array.from(this.pools.values()).find((pool) => pool.gaugeAddress?.equals(gauge))
  }

  // Get all pools
  getAll(): MinimalPoolStore[] {
    return Array.from(this.pools.values())
  }

  // Get system pools only (all pools are system pools)
  getSystemPools(): MinimalPoolStore[] {
    return this.getAll()
  }

  // Get active pools only
  getActivePools(): MinimalPoolStore[] {
    return this.getAll().filter((pool) => pool.isActive)
  }

  findActivePoolsByTokenPair(tokenA: LcAddress, tokenB: LcAddress): MinimalPoolStore[] {
    return this.getActivePools().filter((pool) => {
      const matchesForward = pool.token0.address.equals(tokenA) && pool.token1.address.equals(tokenB)
      const matchesReverse = pool.token0.address.equals(tokenB) && pool.token1.address.equals(tokenA)
      return matchesForward || matchesReverse
    })
  }

  findActivePoolsByToken(token: LcAddress): MinimalPoolStore[] {
    return this.getActivePools().filter((pool) => pool.token0.address.equals(token) || pool.token1.address.equals(token))
  }

  // Get pools by tag
  getPoolsByTag(tag: string): MinimalPoolStore[] {
    return this.getAll().filter((pool) => pool.tags.includes(tag))
  }

  // Get all pools with gauge positions or rewards
  // Shows pools if user has a staked position OR has rewards to claim
  getAllWithGaugeRewards(): MinimalPoolStore[] {
    return this.getAll().filter((pool) => {
      if (!pool.hasGauge) {
        return false
      }
      // Show if user has a staked position (gauge balance or reserves > 0)
      const hasStakedPosition =
        pool.onChainUserGaugeBalance > 0n ||
        pool.onChainUserGaugeReserve0 > 0n ||
        pool.onChainUserGaugeReserve1 > 0n
      // Show if user has rewards to claim
      const hasRewards = pool.onChainUserGaugeGovernanceEarned > 0n
      return hasStakedPosition || hasRewards
    })
  }

  get totalPools(): number {
    return this.pools.size
  }

  get totalTvlUsd(): number {
    return Array.from(this.pools.values()).reduce((total, pool) => {
      return total + pool.tvlUsd
    }, 0)
  }

  clearAllUserValues(): void {
    this.pools.forEach((pool) => {
      pool.onChainUserBalance = 0n
      pool.onChainUserBalanceToken0 = 0n
      pool.onChainUserBalanceToken1 = 0n
      pool.onChainUserClaimableFees0 = 0n
      pool.onChainUserClaimableFees1 = 0n
      pool.onChainUserGaugeBalance = 0n
      pool.onChainUserGaugeGovernanceEarned = 0n
      pool.onChainUserGaugeReserve0 = 0n
      pool.onChainUserGaugeReserve1 = 0n
    })
  }
}
